import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, CreditCard, FileText, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin dashboard panel: list every pillar seller + their payout method, let
 * admins toggle Stripe/Invoice and (optionally) paste a Connect account id
 * manually. Shows pending-manual-payout totals per Invoice seller so the admin
 * can reconcile offline.
 */
export default function SellersPanel() {
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null); // id in-flight

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/sellers`, { withCredentials: true });
            setSellers(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not load sellers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateSeller = async (seller, patch) => {
        setSaving(seller.id);
        try {
            const { data } = await axios.put(
                `${API_URL}/api/admin/sellers/${seller.id}`,
                patch,
                { withCredentials: true }
            );
            setSellers((prev) => prev.map(s => s.id === seller.id ? { ...s, ...data } : s));
            toast.success('Seller updated');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not update');
        } finally {
            setSaving(null);
        }
    };

    if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-[#5B2D8E]" /></div>;

    return (
        <div className="space-y-4" data-testid="sellers-panel">
            <div>
                <h2 className="heading-3">Sellers & payouts</h2>
                <p className="text-sm text-[#4A4A68] mt-1">
                    Each pillar can be set to <strong>Stripe</strong> (direct payouts via Stripe Connect, 7% + €0.50 platform fee)
                    or <strong>Invoice</strong> (platform collects, admin reconciles manually). Items created by admins always go to the main account.
                </p>
            </div>

            <div className="overflow-x-auto border border-[#E5E0D8] rounded-sm">
                <table className="w-full text-sm">
                    <thead className="bg-[#F2EBE5] text-left">
                        <tr>
                            <th className="px-4 py-3">Pillar</th>
                            <th className="px-4 py-3">Payout method</th>
                            <th className="px-4 py-3">Stripe status</th>
                            <th className="px-4 py-3">Pending (invoice)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sellers.map(s => (
                            <SellerRow key={s.id} seller={s} saving={saving === s.id} onUpdate={updateSeller} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SellerRow({ seller, saving, onUpdate }) {
    const [accountId, setAccountId] = useState(seller.stripe_account_id || '');
    const dirty = accountId !== (seller.stripe_account_id || '');

    return (
        <tr className="border-t border-[#E5E0D8] align-top" data-testid={`seller-row-${seller.id}`}>
            <td className="px-4 py-3">
                <p className="font-medium text-[#1A1A2E]">{seller.name || seller.email}</p>
                <p className="text-xs text-[#4A4A68]">{seller.email}</p>
                {seller.role === 'admin' && (
                    <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 bg-[#5B2D8E] text-white rounded-sm uppercase tracking-widest">Admin</span>
                )}
            </td>
            <td className="px-4 py-3">
                <div className="flex gap-1">
                    <button
                        type="button"
                        onClick={() => onUpdate(seller, { payout_method: 'invoice' })}
                        disabled={saving}
                        className={`text-xs px-2.5 py-1.5 rounded-sm border inline-flex items-center gap-1 ${seller.payout_method === 'invoice' ? 'bg-[#5B2D8E] text-white border-[#5B2D8E]' : 'bg-white border-[#E5E0D8] hover:bg-[#F2EBE5]'}`}
                        data-testid={`seller-${seller.id}-invoice`}
                    >
                        <FileText className="w-3 h-3" /> Invoice
                    </button>
                    <button
                        type="button"
                        onClick={() => onUpdate(seller, { payout_method: 'stripe' })}
                        disabled={saving}
                        className={`text-xs px-2.5 py-1.5 rounded-sm border inline-flex items-center gap-1 ${seller.payout_method === 'stripe' ? 'bg-[#5B2D8E] text-white border-[#5B2D8E]' : 'bg-white border-[#E5E0D8] hover:bg-[#F2EBE5]'}`}
                        data-testid={`seller-${seller.id}-stripe`}
                    >
                        <CreditCard className="w-3 h-3" /> Stripe
                    </button>
                </div>
            </td>
            <td className="px-4 py-3">
                {seller.payout_method === 'stripe' ? (
                    <>
                        {seller.stripe_charges_enabled ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700">
                                <CheckCircle2 className="w-3 h-3" /> Ready
                            </span>
                        ) : seller.stripe_account_id ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                                <AlertCircle className="w-3 h-3" /> Onboarding incomplete
                            </span>
                        ) : (
                            <span className="text-xs text-[#4A4A68]">Not connected</span>
                        )}
                        <div className="mt-2 flex gap-1">
                            <input
                                type="text"
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                placeholder="acct_..."
                                className="flex-1 text-xs px-2 py-1 border border-[#E5E0D8] rounded-sm"
                                data-testid={`seller-${seller.id}-account-id`}
                            />
                            {dirty && (
                                <button
                                    type="button"
                                    onClick={() => onUpdate(seller, { stripe_account_id: accountId })}
                                    disabled={saving}
                                    className="btn-primary text-xs px-2 py-1 inline-flex items-center gap-1"
                                    data-testid={`seller-${seller.id}-save-account`}
                                >
                                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <span className="text-xs text-[#4A4A68]">—</span>
                )}
            </td>
            <td className="px-4 py-3 text-xs">
                {(seller.pending_invoice_totals || []).length === 0 ? (
                    <span className="text-[#4A4A68]">None</span>
                ) : (
                    (seller.pending_invoice_totals || []).map((p) => (
                        <div key={p.currency} className="text-[#1A1A2E]">
                            {p.total.toFixed(2)} {p.currency?.toUpperCase() || 'EUR'} <span className="text-[#4A4A68]">({p.count} sales)</span>
                        </div>
                    ))
                )}
            </td>
        </tr>
    );
}
